// ════════════════════════════════════════════════════════════
// _authed — ECR
// ════════════════════════════════════════════════════════════
// Layout route pathless (`_authed` n'apparaît pas dans l'URL).
// Toutes les routes enfants (dans /routes/_authed/) sont
// automatiquement :
//   1. Protégées par auth (redirect vers /login sinon)
//   2. Wrappées dans <Layout> (shell complet)
// ════════════════════════════════════════════════════════════
import { useEffect } from 'react';
import {
  createFileRoute,
  Outlet,
  redirect,
  useNavigate,
  useLocation,
} from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { AppShell } from '@/components/shell';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/store/useAuthStore';
import { canAccessPath, landingRouteForRole } from '@/types/auth';
import { t } from '@/lib/i18n';

export const Route = createFileRoute('/_authed')({
  beforeLoad: ({ location }) => {
    const { session, isLoading } = useAuthStore.getState();

    // Si bootstrap déjà fini et pas de session → redirect direct.
    // Si bootstrap pas encore fini, on laisse passer : le component
    // gère la redirection réactivement via useEffect ci-dessous.
    if (!isLoading && !session) {
      // TanStack Router utilise volontairement `throw redirect(...)`
      // pour interrompre le rendu et déclencher la navigation.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        to: '/login',
        search: { redirect: location.href },
      });
    }
  },
  component: AuthedLayout,
});

function AuthedLayout() {
  const { isLoading, session, profile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Si bootstrap fini ET pas de session → redirige (réactivement)
  useEffect(() => {
    if (!isLoading && !session) {
      void navigate({
        to: '/login',
        search: { redirect: location.href },
      });
    }
  }, [isLoading, session, navigate, location.href]);

  // ─── Garde-fou accès par rôle (V1 fidèle) — redirection IMPÉRATIVE ──
  // On redirige via useEffect (pas en rendant <Navigate> pendant le render,
  // qui provoque une boucle de transition router → React #185).
  const accessDenied = Boolean(
    session && profile && !canAccessPath(profile.role, location.pathname),
  );
  useEffect(() => {
    if (accessDenied && profile) {
      void navigate({ to: landingRouteForRole(profile.role), replace: true });
    }
  }, [accessDenied, profile, navigate]);

  // Bootstrap en cours → spinner plein écran
  if (isLoading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-2">
        <Loader2 className="text-primary size-6 animate-spin" />
        <p className="text-muted-foreground text-sm">{t('common.loading')}</p>
      </div>
    );
  }

  // Bootstrap fini mais pas de session → en attente de la redirection
  if (!session) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center">
        <Loader2 className="text-primary size-6 animate-spin" />
      </div>
    );
  }

  // ─── Garde-fou accès par rôle (V1 fidèle) — FAIL CLOSED ─────
  // Session présente mais profil pas encore chargé → on N'AFFICHE PAS
  // l'app tant qu'on ne connaît pas le rôle (sinon trou fail-open où
  // un pointeur pourrait charger /paiements une fraction de seconde).
  if (!profile) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-2">
        <Loader2 className="text-primary size-6 animate-spin" />
        <p className="text-muted-foreground text-sm">{t('common.loading')}</p>
      </div>
    );
  }

  // Accès refusé → spinner pendant que le useEffect ci-dessus redirige
  // (on n'affiche jamais le contenu non autorisé).
  if (accessDenied) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center">
        <Loader2 className="text-primary size-6 animate-spin" />
      </div>
    );
  }

  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}
